import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SquarePaymentCard from '../components/SquarePaymentCard';
import ErrorModal from '../components/ErrorModal';
import { prysmDB } from '../utils/database';

const Payment = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [alreadyCompleted, setAlreadyCompleted] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const checkExistingRegistration = async () => {
            try {
                const existingPayment = await prysmDB.get('payment_result', 'current_payment');
                if (existingPayment?.success) {
                    setAlreadyCompleted(true);
                } else {
                    const registrationData = await prysmDB.get('registration_data', 'current_registration');
                    if (registrationData?.paymentCompleted) {
                        setAlreadyCompleted(true);
                    }
                }
            } catch (err) {
                console.error('[Payment] Error checking registration status:', err);
            } finally {
                setIsLoading(false);
            }
        };

        checkExistingRegistration();
    }, []);

    const handleSuccess = async (result) => {
        try {
            const registrationData = await prysmDB.get('registration_data', 'current_registration');
            if (registrationData) {
                registrationData.paymentCompleted = true;
                registrationData.paymentTimestamp = new Date().toISOString();
                await prysmDB.set('registration_data', 'current_registration', registrationData);
                window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: registrationData }));
            }
        } catch (err) {
            console.error('[Payment] Error updating registration data after success:', err);
        }
    };

    const handleError = (err) => {
        setError({ message: err.message || 'Registration failed' });
        setShowErrorModal(true);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-white text-lg">Loading...</p>
                </div>
            </div>
        );
    }

    if (alreadyCompleted) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4">
                <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-gray-700 max-w-md w-full text-center">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-900/50 mb-6">
                        <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-100 mb-4">Already Registered</h1>
                    <p className="text-gray-400 mb-8">Your registration has already been completed.</p>
                    <button
                        onClick={() => navigate('/upload-selfie')}
                        className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white py-3 px-4 rounded-xl font-medium hover:from-pink-600 hover:to-purple-700 transition-all duration-300"
                    >
                        Continue to Next Step
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <SquarePaymentCard
                onPaymentSuccess={handleSuccess}
                onPaymentError={handleError}
            />
            <ErrorModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                error={error}
                title="Registration Error"
                showRetry={true}
                onRetry={() => { setError(null); setShowErrorModal(false); }}
            />
        </div>
    );
};

export default Payment;
